import React from 'react';
import styled, { css } from 'styled-components';
import { MissionHistory } from '../../types/missionHistory';
import missionHistoryService from '../../services/missionHistoryService';

const isModernTheme = (theme: any) => theme.name === 'modernWarfare';

const MissionTable = styled.div`
  background: ${({ theme }) => isModernTheme(theme) 
    ? 'rgba(5, 7, 10, 0.6)' 
    : 'var(--legacy-card-bg)'};
  border: 1px solid ${({ theme }) => isModernTheme(theme) 
    ? 'rgba(255, 255, 255, 0.1)' 
    : '#00d4ff'};
  border-radius: ${({ theme }) => isModernTheme(theme) ? '4px' : '8px'};
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  ${({ theme }) => isModernTheme(theme) && css`
    clip-path: polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px);
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}
`;

const MissionTableHeader = styled.div<{ $showResult: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$showResult
    ? '170px 100px 200px 140px 90px 90px 90px 90px'
    : '170px 100px 200px 140px 110px 90px 90px'
  };
  background: ${({ theme }) => isModernTheme(theme) 
    ? 'rgba(5, 7, 10, 0.8)' 
    : 'rgba(0, 212, 255, 0.1)'};
  border-bottom: 1px solid ${({ theme }) => isModernTheme(theme) 
    ? 'rgba(255, 255, 255, 0.1)' 
    : '#00d4ff'};
  padding: 15px 20px;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.secondary : '#00d4ff'};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
  letter-spacing: ${({ theme }) => isModernTheme(theme) ? '1px' : 'normal'};
`;

const MissionTableRow = styled.div<{ $faction: string; $showResult: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$showResult
    ? '170px 100px 200px 140px 90px 90px 90px 90px'
    : '170px 100px 200px 140px 110px 90px 90px'
  };
  padding: 15px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
  
  /* Background color based on faction */
  background: ${props => {
    if (isModernTheme(props.theme)) {
      return 'rgba(255, 255, 255, 0.02)';
    }
    return props.$faction === 'human'
      ? 'rgba(59, 130, 246, 0.08)'
      : props.$faction === 'alien'
        ? 'rgba(255, 136, 0, 0.08)'
        : 'rgba(255, 255, 255, 0.02)';
  }};

  /* Left border indicator */
  border-left: ${props => {
    if (isModernTheme(props.theme)) {
      return props.$faction === 'human'
        ? `2px solid ${props.theme.colors.secondary}`
        : props.$faction === 'alien'
          ? `2px solid ${props.theme.colors.primary}`
          : '2px solid transparent';
    }
    return props.$faction === 'human'
      ? '4px solid #3b82f6'
      : props.$faction === 'alien'
        ? '4px solid #ff8800'
        : '4px solid transparent';
  }};

  &:hover {
    background: ${props => {
      if (isModernTheme(props.theme)) {
        return 'rgba(255, 255, 255, 0.05)';
      }
      return props.$faction === 'human'
        ? 'rgba(59, 130, 246, 0.15)'
        : props.$faction === 'alien'
          ? 'rgba(255, 136, 0, 0.15)'
          : 'rgba(59, 130, 246, 0.05)';
    }};

    box-shadow: ${props => {
      if (isModernTheme(props.theme)) return 'none';
      return props.$faction === 'human'
        ? '0 2px 8px rgba(59, 130, 246, 0.2)'
        : props.$faction === 'alien'
          ? '0 2px 8px rgba(255, 136, 0, 0.2)'
          : '0 2px 8px rgba(59, 130, 246, 0.1)';
    }};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const MissionTableCell = styled.div`
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.text : '#fff'};
  font-size: 14px;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MissionDate = styled.div`
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.textSecondary : '#00d4ff'};
  font-size: 12px;
`;

const MissionType = styled.div<{ $gameType: string }>`
  color: ${props => {
    if (isModernTheme(props.theme)) return props.theme.colors.text;
    return props.$gameType === 'airsoft' ? '#4CAF50' : '#FF9800';
  }};
  font-weight: 500;
  text-transform: uppercase;
  
  ${({ theme }) => isModernTheme(theme) && css`
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 2px;
    font-size: 11px;
  `}
`;

const FieldName = styled.div`
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.text : '#fff'};
  font-size: 13px;
`;

const RefereeName = styled.div`
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.textMuted : '#ccc'};
  font-size: 13px;
`;

const FactionBadge = styled.div<{ $faction: string }>`
  background: ${props => {
    if (isModernTheme(props.theme)) {
      return props.$faction === 'human' 
        ? withAlpha(props.theme.colors.secondary, 0.2)
        : props.$faction === 'alien'
          ? withAlpha(props.theme.colors.primary, 0.2)
          : 'rgba(255, 255, 255, 0.1)';
    }
    return missionHistoryService.getFactionColor(props.$faction);
  }};
  color: ${props => {
    if (isModernTheme(props.theme)) {
      return props.$faction === 'human' 
        ? props.theme.colors.secondary
        : props.$faction === 'alien'
          ? props.theme.colors.primary
          : props.theme.colors.text;
    }
    return '#fff';
  }};
  padding: 4px 8px;
  border-radius: ${({ theme }) => isModernTheme(theme) ? '2px' : '4px'};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  text-align: center;
  min-width: 60px;
  border: ${({ theme, $faction }) => {
    if (isModernTheme(theme)) {
      return $faction === 'human'
        ? `1px solid ${theme.colors.secondary}`
        : $faction === 'alien'
          ? `1px solid ${theme.colors.primary}`
          : '1px solid rgba(255, 255, 255, 0.2)';
    }
    return 'none';
  }};
`;

const ResultBadge = styled.div<{ $result: string }>`
  background: ${props => {
    if (isModernTheme(props.theme)) {
      return props.$result === 'victory' 
        ? withAlpha(props.theme.colors.success, 0.2)
        : props.$result === 'defeat'
          ? withAlpha(props.theme.colors.error, 0.2)
          : withAlpha(props.theme.colors.textMuted, 0.2);
    }
    return missionHistoryService.getResultColor(props.$result);
  }};
  color: ${props => {
    if (isModernTheme(props.theme)) {
      return props.$result === 'victory' 
        ? props.theme.colors.success
        : props.$result === 'defeat'
          ? props.theme.colors.error
          : props.theme.colors.textMuted;
    }
    return '#fff';
  }};
  padding: 4px 8px;
  border-radius: ${({ theme }) => isModernTheme(theme) ? '2px' : '4px'};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  text-align: center;
  min-width: 60px;
  border: ${({ theme, $result }) => {
    if (isModernTheme(theme)) {
      return $result === 'victory'
        ? `1px solid ${theme.colors.success}`
        : $result === 'defeat'
          ? `1px solid ${theme.colors.error}`
          : `1px solid ${theme.colors.textMuted}`;
    }
    return 'none';
  }};
`;

const PointsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PointsEarned = styled.span<{ $points: number }>`
  color: ${props => {
    if (isModernTheme(props.theme)) return props.theme.colors.text;
    return props.$points >= 10 ? '#4CAF50' : props.$points >= 5 ? '#FF9800' : '#F44336';
  }};
  font-weight: 600;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const BonusPoints = styled.span`
  color: #ffd700;
  font-weight: 500;
  font-size: 12px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const GameDuration = styled.div`
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.textMuted : '#ccc'};
  font-size: 13px;
  text-align: left;
`;

const NoMissionsMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #666;
  font-size: 16px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const LoadingMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => isModernTheme(theme) ? theme.colors.primary : '#00d4ff'};
  font-size: 16px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const ErrorMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #F44336;
  font-size: 16px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

// Helper for Alpha colors
const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface MissionHistoryListProps {
  missions: MissionHistory[];
  loading: boolean;
  error: string | null;
  onMissionClick?: (mission: MissionHistory) => void;
  showResultColumn?: boolean;    // показывать колонку RESULT (default: true)
  showPlayerFaction?: boolean;   // показывать фракцию игрока vs победившую фракцию (default: true)  
  factionLabel?: string;         // текст заголовка фракции (default: "FACTION")
}

const MissionHistoryList: React.FC<MissionHistoryListProps> = ({
  missions,
  loading,
  error,
  onMissionClick,
  showResultColumn = true,
  showPlayerFaction = true,
  factionLabel = "FACTION"
}) => {
  if (loading) {
    return (
      <MissionTable>
        <LoadingMessage>Loading mission history...</LoadingMessage>
      </MissionTable>
    );
  }

  if (error) {
    return (
      <MissionTable>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </MissionTable>
    );
  }

  if (missions.length === 0) {
    return (
      <MissionTable>
        <NoMissionsMessage>No missions found</NoMissionsMessage>
      </MissionTable>
    );
  }

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <MissionTable>
      <MissionTableHeader $showResult={showResultColumn}>
        <div>DATE</div>
        <div>TYPE</div>
        <div>FIELD</div>
        <div>REFEREE</div>
        <div>{factionLabel}</div>
        {showResultColumn && <div>RESULT</div>}
        <div>POINTS</div>
        <div>DURATION</div>
      </MissionTableHeader>

      {missions.map((mission) => (
        <MissionTableRow
          key={mission.id}
          $faction={mission.playerFaction?.toLowerCase() || ''}
          $showResult={showResultColumn}
          onClick={() => onMissionClick?.(mission)}
        >
          <MissionTableCell>
            <MissionDate>
              {mission.missionDate ? missionHistoryService.formatDateTime(mission.missionDate) : 'Unknown'}
            </MissionDate>
          </MissionTableCell>

          <MissionTableCell>
            <MissionType $gameType={mission.gameType || 'unknown'}>
              {mission.gameType || 'Unknown'}
            </MissionType>
          </MissionTableCell>

          <MissionTableCell>
            <FieldName title={mission.fieldName || 'Unknown Field'}>
              {mission.fieldName || 'Unknown Field'}
            </FieldName>
          </MissionTableCell>

          <MissionTableCell>
            <RefereeName title={mission.refereeName || 'Unknown Referee'}>
              {mission.refereeName || 'Unknown Referee'}
            </RefereeName>
          </MissionTableCell>

          <MissionTableCell>
            <FactionBadge $faction={mission.playerFaction || 'unknown'}>
              {mission.playerFaction ? missionHistoryService.getFactionDisplayName(mission.playerFaction) : 'Unknown'}
            </FactionBadge>
          </MissionTableCell>

          {showResultColumn && (
            <MissionTableCell>
              <ResultBadge $result={mission.result || 'unknown'}>
                {mission.result ? missionHistoryService.getResultDisplayName(mission.result) : 'Unknown'}
              </ResultBadge>
            </MissionTableCell>
          )}

          <MissionTableCell>
            <PointsWrapper>
              <PointsEarned $points={mission.pointsEarned || 0}>
                +{mission.pointsEarned || 0}
              </PointsEarned>
              {mission.bonusPoints > 0 && (
                <BonusPoints>(+{mission.bonusPoints})</BonusPoints>
              )}
            </PointsWrapper>
          </MissionTableCell>

          <MissionTableCell>
            <GameDuration>
              {mission.gameDuration ? formatDuration(mission.gameDuration) : 'Unknown'}
            </GameDuration>
          </MissionTableCell>
        </MissionTableRow>
      ))}
    </MissionTable>
  );
};

export default MissionHistoryList;